import React, { useState } from 'react';
import { Eye, EyeOff, ArrowUp, ArrowDown, Trash2, Plus, Check, X } from 'lucide-react';
import { ADDABLE_TYPES, GENRES, DECADES, SORT_OPTIONS, newSection, sectionNote } from '../config/sectionTypes';

// Edit-mode UI for the home dashboard: reorder (up/down), rename, show/hide,
// remove, and add carousels. Works on a local draft; commits via onSave.
export default function DashboardEditor({ initialSections, onSave, onCancel, saving }) {
  const [sections, setSections] = useState(initialSections.map((s) => ({ ...s })));
  const [addType, setAddType] = useState(ADDABLE_TYPES[0] ? ADDABLE_TYPES[0].type : '');

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const copy = [...sections];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setSections(copy);
  };
  const update = (i, patch) => setSections(sections.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const remove = (i) => setSections(sections.filter((_, k) => k !== i));
  const add = () => { if (addType) setSections([...sections, newSection(addType)]); };

  return (
    <div className="bg-white py-8 px-4 border-t border-gray-100">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Customize your home</h2>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm rounded-lg border border-videbits-200 text-videbits-700 hover:bg-videbits-50 flex items-center gap-1"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={() => onSave(sections)}
              disabled={saving}
              className="px-3 py-1.5 text-sm rounded-lg bg-videbits-600 text-white hover:bg-videbits-700 disabled:bg-gray-300 disabled:text-gray-500 flex items-center gap-1"
            >
              <Check className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {sections.length === 0 && (
          <p className="text-sm text-gray-400 mb-3">No carousels yet — add one below.</p>
        )}

        <ul className="space-y-2">
          {sections.map((s, i) => (
            <li
              key={s.id}
              className={`flex items-center gap-2 p-2 rounded-lg border border-gray-100 ${s.visible === false ? 'bg-gray-100 opacity-60' : 'bg-gray-50'}`}
            >
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-videbits-500 hover:text-videbits-700 disabled:text-gray-400 disabled:opacity-30" aria-label="Move up"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === sections.length - 1} className="text-videbits-500 hover:text-videbits-700 disabled:text-gray-400 disabled:opacity-30" aria-label="Move down"><ArrowDown className="w-4 h-4" /></button>
              </div>
              <input
                value={s.title}
                onChange={(e) => update(i, { title: e.target.value })}
                maxLength={80}
                className="flex-1 px-2 py-1 text-sm rounded border border-gray-200 bg-white"
                aria-label="Section title"
              />
              {s.type === 'genre' && (
                <select
                  value={(s.params && s.params.genre) || ''}
                  onChange={(e) => update(i, { params: { ...s.params, genre: e.target.value } })}
                  className="px-2 py-1 text-xs rounded border border-gray-200 bg-white"
                  aria-label="Genre"
                >
                  {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              )}
              {s.type === 'discover' && (
                <div className="flex gap-1">
                  <select
                    value={(s.params && s.params.decade) || ''}
                    onChange={(e) => update(i, { params: { ...s.params, decade: e.target.value ? Number(e.target.value) : null } })}
                    className="px-2 py-1 text-xs rounded border border-gray-200 bg-white"
                    aria-label="Decade"
                  >
                    <option value="">Any decade</option>
                    {DECADES.map((d) => <option key={d} value={d}>{d}s</option>)}
                  </select>
                  <select
                    value={(s.params && s.params.ratingFrom) != null ? s.params.ratingFrom : ''}
                    onChange={(e) => update(i, { params: { ...s.params, ratingFrom: e.target.value ? Number(e.target.value) : null } })}
                    className="px-2 py-1 text-xs rounded border border-gray-200 bg-white"
                    aria-label="Minimum rating"
                  >
                    <option value="">Any ★</option>
                    {[6, 7, 8, 9].map((r) => <option key={r} value={r}>★ {r}+</option>)}
                  </select>
                  <select
                    value={(s.params && s.params.sortBy) || 'popularity.desc'}
                    onChange={(e) => update(i, { params: { ...s.params, sortBy: e.target.value } })}
                    className="px-2 py-1 text-xs rounded border border-gray-200 bg-white"
                    aria-label="Sort by"
                  >
                    {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              )}
              <span className="text-xs text-gray-400 hidden sm:block w-44 truncate" title={sectionNote(s)}>{sectionNote(s)}</span>
              <button onClick={() => update(i, { visible: s.visible === false })} className="text-videbits-500 hover:text-videbits-700" aria-label="Toggle visibility">
                {s.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => remove(i)} className="text-videbits-500 hover:text-red-600" aria-label="Remove section"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 mt-4">
          <select
            value={addType}
            onChange={(e) => setAddType(e.target.value)}
            className="px-2 py-1.5 text-sm rounded-lg border border-gray-200 bg-white"
            aria-label="Carousel type to add"
          >
            {ADDABLE_TYPES.map(({ type, label }) => (
              <option key={type} value={type}>{label}</option>
            ))}
          </select>
          <button
            onClick={add}
            className="px-3 py-1.5 text-sm rounded-lg border border-videbits-200 text-videbits-700 hover:bg-videbits-50 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add carousel
          </button>
        </div>
      </div>
    </div>
  );
}
